import { TeamResponse, UserResponse } from '../../domain/dtos/upsert-championship-application-response.dto';
import { createAction, createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';

import { AnnouncedTeamStatus } from '../../domain/enums/announced-team-status';
import { AnnouncedTeamsMap } from '../../domain/interfaces/announced-teams-map';
import { ApplicationStatus } from 'modules/applications/domain/enums/application-status';
import { CreateUserInputDto } from 'modules/teams/domain/dtos/create-user-input.dto';
import { PlayerApplication } from '../../domain/interfaces/player-application';
import { PlayerPosition } from '../../domain/enums/player-position';
import { RootState } from 'store/store';
import { Team } from '../../domain/interfaces/team';
import { UpsertChampionshipApplicationDto } from 'modules/teams/domain/dtos/upsert-championship-application.dto';
import { UpsertChampionshipApplicationUserDto } from '../../domain/dtos/upsert-championship-application-user.dto';
import { UpsertTournamentTeamDto } from '../../domain/dtos/upsert-tournament-team.dto';
import { addChampionshipTeam } from 'modules/tournaments/store/championship/actions';
import teamsRepository from '../../domain/repositories/teams.repository';
import { createTeam } from 'modules/teams/store/teams';

const PREFIX = 'teams/championshipTeams';

// TODO: check and maybe remove
export const createChampionshipTeam = createAsyncThunk<Team, UpsertTournamentTeamDto, { state: RootState }>(
  `${PREFIX}/createChampionshipTeam`,
  async (payload, { dispatch }) => {
    try {
      const result = await dispatch(createTeam(payload));
      const team = unwrapResult(result);
      await dispatch(addChampionshipTeam(team.id));

      return team;
    } catch (err) {
      console.error(`${PREFIX}/createChampionshipTeam error:`, err);
      throw err;
    }
  },
);

export const getChampionshipAnnouncedTeams = createAsyncThunk<AnnouncedTeamsMap, { championshipId: number }>(
  `${PREFIX}/getChampionshipAnnouncedTeams`,
  async (payload, { getState }) => {
    try {
      return await teamsRepository.getAnnouncedChampionshipTeams(payload.championshipId);
    } catch (err) {
      console.error(`${PREFIX}/getChampionshipAnnouncedTeams error:`, err);
      throw err;
    }
  },
);

export const upsertChampionshipPlayerApplication = createAsyncThunk<PlayerApplication, UpsertChampionshipApplicationUserDto>(
  `${PREFIX}/upsertChampionshipPlayerApplication`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const result = await teamsRepository.upsertChampionshipApplicationForUser(payload);
      await dispatch(getChampionshipAnnouncedTeams({ championshipId: payload.championshipId }));

      return result;
    } catch (error) {
      console.error(`${PREFIX}/upsertChampionshipPlayerApplication error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);

      return rejectWithValue(message);
    }
  },
);

export const championshipPlayerApplicationChangeNumber = createAsyncThunk<
  PlayerApplication,
  Omit<UpsertChampionshipApplicationUserDto, 'id'> & { id: number }
>(`${PREFIX}/championshipPlayerApplicationChangeNumber`, async (payload, { dispatch, rejectWithValue }) => {
  try {
    const result = await teamsRepository.upsertChampionshipApplicationForUser(payload);

    dispatch(
      playerChampionshipApplicationNumberChanged({
        id: payload.id,
        teamId: payload.teamId,
        number: payload.createUserOrUpdateApplication?.number,
        championshipId: payload.championshipId,
      }),
    );

    return result;
  } catch (error) {
    console.error(`${PREFIX}/championshipPlayerApplicationChangeNumber error:`, error);

    let message;
    if (error instanceof Error) message = error.message;
    else message = String(error);

    return rejectWithValue(message);
  }
});

export const championshipPlayerApplicationChangePosition = createAsyncThunk<
  PlayerApplication,
  Omit<UpsertChampionshipApplicationUserDto, 'id'> & { id: number }
>(`${PREFIX}/championshipPlayerApplicationChangePosition`, async (payload, { dispatch, rejectWithValue }) => {
  try {
    const result = await teamsRepository.upsertChampionshipApplicationForUser(payload);

    dispatch(
      playerChampionshipApplicationPositionChanged({
        id: payload.id,
        teamId: payload.teamId,
        position: payload.createUserOrUpdateApplication?.position as PlayerPosition,
        championshipId: payload.championshipId,
      }),
    );

    return result;
  } catch (error) {
    console.error(`${PREFIX}/championshipPlayerApplicationChangeNumber error:`, error);

    let message;
    if (error instanceof Error) message = error.message;
    else message = String(error);

    return rejectWithValue(message);
  }
});

export const upsertChampionshipTeamApplication = createAsyncThunk<
  { teamId: number; status: AnnouncedTeamStatus; applicationStatus: ApplicationStatus } | null,
  { teamId: number; status: AnnouncedTeamStatus; championshipId: number }
>(`${PREFIX}/upsertChampionshipTeamApplication`, async (payload, { getState }) => {
  try {
    const state = getState() as RootState;
    const { data } = state.teams.championshipTeams;
    if (!data) {
      return null;
    }

    let applicationStatus: ApplicationStatus;
    switch (payload.status) {
      case AnnouncedTeamStatus.ACCEPTED:
        applicationStatus = ApplicationStatus.ACCEPTED;
        break;
      case AnnouncedTeamStatus.DECLINED:
        applicationStatus = ApplicationStatus.DECLINED;
        break;
      default:
        applicationStatus = ApplicationStatus.PENDING;
    }

    const apps: PlayerApplication[] = data.byId[payload.championshipId][payload.teamId].applications;
    const createUserOrUpdateApplications: CreateUserInputDto[] = apps.map((app) => ({
      userId: app.player.id,
      name: app.player.firstName,
      surname: app.player.lastName,
      middleName: app.player.middleName,
      birthDate: app.player.birthDate,
      weight: app.player.weight,
      height: app.player.height,
      number: app.number,
      position: app.position ? app.position.toUpperCase() : app.position,
      state: applicationStatus.toUpperCase() as ApplicationStatus,
    }));

    const applicationPayload: UpsertChampionshipApplicationDto = {
      teamId: payload.teamId,
      championshipId: apps[0].championshipId,
      applications: createUserOrUpdateApplications,
    };

    await teamsRepository.upsertChampionshipApplication(applicationPayload);

    return {
      ...payload,
      applicationStatus,
    };
  } catch (err) {
    console.error(`${PREFIX}/upsertChampionshipTeamApplication error:`, err);
    throw err;
  }
});

// export const announceNewTeam = createAction<Team>(`${PREFIX}/announceNewTeam`);
export const announceNewTeam = createAsyncThunk<
  { team: Team; championshipId: number },
  { team: Team; championshipId: number },
  { state: RootState }
>(`${PREFIX}/announceNewTeam`, async (payload, { dispatch, getState }) => {
  try {
    const state = getState() as RootState;
    const championship = state.tourneys.championship.data!.byId[payload.championshipId];
    await dispatch(addChampionshipTeam(payload.team.id));
    return { team: payload.team, championshipId: championship.id };
  } catch (err) {
    console.error(`${PREFIX}/announceNewTeam error:`, err);
    throw err;
  }
});

export const playerChampionshipApplicationNumberChanged = createAction<{
  teamId: number;
  id: number;
  number?: number;
  championshipId: number;
}>(`${PREFIX}/playerChampionshipApplicationNumberChanged`);

export const playerChampionshipApplicationPositionChanged = createAction<{
  teamId: number;
  id: number;
  position?: PlayerPosition;
  championshipId: number;
}>(`${PREFIX}/playerChampionshipApplicationPositionChanged`);

export const upsertChampionshipApplicationUsers = createAsyncThunk<
  { team: TeamResponse; applications: UserResponse[] } | null,
  UpsertChampionshipApplicationDto
>(`${PREFIX}/upsertChampionshipApplicationUsers`, async (payload, { getState, rejectWithValue }) => {
  try {
    const state = getState() as RootState;
    const { data } = state.teams.championshipTeams;
    if (!data) {
      return null;
    }

    const applicationPayload: UpsertChampionshipApplicationDto = {
      teamId: payload.teamId,
      championshipId: payload.championshipId,
      applications: payload.applications,
    };

    const { team, users } = await teamsRepository.upsertChampionshipApplication(applicationPayload);

    return {
      team,
      applications: users,
    };
  } catch (error) {
    console.error(`${PREFIX}/upsertChampionshipTeamApplication error:`, error);

    let message;
    if (error instanceof Error) message = error.message;
    else message = String(error);

    return rejectWithValue(message);
  }
});
